/**
 * অবস্থা ১ · নতুন ব্যবহারকারী — `P0 States § ১`, `§ ২` and `§ ৪`.
 *
 * THREE FRAMES, ONE MATRIX ROW. The design draws the empty state three times — once
 * on home (`১ · নতুন ব্যবহারকারীর হোম`), once on activity (`২ · কোনো অর্ডার নেই`) and
 * once on AI (`৪ · কোনো এআই ইনসাইট নেই`) — and the matrix carries one row,
 * `নতুন ব্যবহারকারী`, across five surfaces. So this is one component with a `variant`
 * naming which frame it is, not three components: the row is what is implemented, and
 * a fourth surface that needs it (account, internal) picks the nearest drawn variant
 * rather than getting a fourth drawing invented for it.
 *
 * Frame note (§ ১): "Four quick actions instead of eight, because a first-time user
 * has no history to rank them by. The empty state promises what happens after one use
 * — that is the only thing worth saying here."
 *
 * DEVIATIONS, reported not resolved:
 *  · The tab bar is `@oprappo/ui`'s `TabBar`. § 03 draws it at 22px icons and
 *    `pt-[9px] pb-[10px]`; these frames draw 21px and `padding:8px 0 9px`, with the
 *    inactive stroke `#5B6B69` rather than `#8B9795` and the bottom-bar shadow at .10
 *    rather than the sheet's .14. E1Activity made the same substitution for the same
 *    reason — the component is not forked.
 *  · § ২'s empty block is drawn centred in the flex body at `padding:24px`, with a
 *    52px glyph, a `600 20px/1.75` title and a filled 15px action — not the
 *    `EmptyState` card from § 07 (bordered, 17px title, 48px Button). The frame is
 *    transcribed; `EmptyState` is left for the surfaces the design system drew it on.
 *  · § ৪'s "কোনো এআই ইনসাইট নেই" is drawn as an AI answer that the domain is not live.
 *    It is therefore ALSO the AI-surface presentation of `সেবাটি আসছে`. Both matrix
 *    rows are `হ্যাঁ` on the AI column, and the design draws one frame for the pair.
 *    Flagged as an ambiguous cell; kept under this row because the frame's own title
 *    is `Empty · AI`.
 *
 * W1-04 · WHAT CHANGED, AND WHAT IT COSTS THE GATE (`docs/deviations/W1-04.md`, S1 and S4):
 *  · § ১ greets the person by their OWN name or by nobody — never the drawn one — and no
 *    longer lists "সবচেয়ে বেশি যা করা হয়", a ranking a product with no history cannot
 *    have. Its quick actions and its search bar are buttons that go somewhere.
 *  · § ৪ is an empty conversation. The drawn exchange was one imagined person’s.
 *  · Every control in all three has a handler, passed in by `boundary.tsx`.
 *
 * Reachable ONLY through the `?state=` seam, which is closed in every build that can
 * reach the real API (`boundary.tsx § stateSeamOpen`): no error code produces this
 * state, so a customer never sees it. What the gate measures here is still the product.
 */
import { TabBar, type TabBarKey } from '@oprappo/ui';
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame, BackChevron } from './chrome.js';
import { NEW_USER_HOME, NEW_USER_ACTIVITY, NEW_USER_AI } from './data.js';

export type NewUserVariant = 'home' | 'activity' | 'ai';

export interface NewUserStateProps {
  variant: NewUserVariant;
  /**
   * The person's own name, as this device last heard it from the server
   * (`auth/session.ts § cachedDisplayName`) — or nothing, and the greeting stands alone.
   * Never the name the frame was drawn for.
   */
  nameBn?: string | null | undefined;
  /** § ২'s কিছু খুঁজে দেখুন, and § ৪'s composer. */
  onAction?: (() => void) | undefined;
  /** § ১'s search field, which is a door to `/search`, not an input. */
  onSearch?: (() => void) | undefined;
  /** § ১'s four quick actions, by id. */
  onQuickAction?: ((id: string) => void) | undefined;
  /** § ৪'s back chevron. */
  onBack?: (() => void) | undefined;
  /** The customer tab bar under § ১ and § ২. */
  onTab?: ((key: TabBarKey) => void) | undefined;
}

type Handlers = Omit<NewUserStateProps, 'variant'>;

/* ─────────────────────────────────────────────────────────────────── § ১ ── */

/** § ১'s four quick-action glyphs, 24px, 2px, teal. */
function QuickGlyph({ id }: { id: string }): JSX.Element {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden>
      {id === 'recharge' ? (
        <>
          <rect x="7" y="2.5" width="10" height="19" rx="1.5" stroke="#0E3A45" strokeWidth={2} />
          <path d="M10 18.5h4" stroke="#0E3A45" strokeWidth={2} />
        </>
      ) : id === 'bill' ? (
        <>
          <rect x="3" y="4" width="18" height="16" rx="1.5" stroke="#0E3A45" strokeWidth={2} />
          <path d="M3 9h18" stroke="#0E3A45" strokeWidth={2} />
        </>
      ) : id === 'food' ? (
        // Not § 01's `market`: that draws `M3.5 7h17l-1.5 13h-14z` with a `M9 7V5`
        // handle. § ১ draws a shorter bag with a wider handle. Transcribed as drawn.
        <>
          <path d="M4 8h16l-1.5 12h-13z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
          <path d="M8.5 8V6a3.5 3.5 0 017 0v2" stroke="#0E3A45" strokeWidth={2} />
        </>
      ) : (
        <>
          <path d="M3 15h18M6 15V9l3-3h6l3 3v6" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
          <circle cx="8" cy="18" r="1.6" stroke="#0E3A45" strokeWidth={2} />
          <circle cx="16" cy="18" r="1.6" stroke="#0E3A45" strokeWidth={2} />
        </>
      )}
    </svg>
  );
}

function NewUserHome({ nameBn, onSearch, onQuickAction, onTab }: Handlers): JSX.Element {
  const d = NEW_USER_HOME;
  const name = nameBn === null || nameBn === undefined || nameBn.trim() === '' ? null : nameBn;
  return (
    <StateFrame stateKey="new_user">
      <div className="bg-teal px-[16px] pt-[16px] pb-[18px]">
        <div className="flex justify-between items-center mb-[14px]">
          <div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-on-teal-muted">
              {d.greeting_bn}
            </div>
            {/* The person's own name, or none. See `NewUserStateProps.nameBn`. */}
            {name === null ? null : (
              <div className="font-anek font-semibold text-[17px] leading-[1.75] text-white mt-[1px]">
                {name}
              </div>
            )}
          </div>
          {/* The § 01 accessible name for the bell is `খবর — {n}টি নতুন`; this frame is
              drawn with no count, so no count is invented and the control is decorative
              here. Reported. */}
          <div className="box-content w-[34px] h-[34px] border border-white/[.32] rounded-full flex items-center justify-center">
            <svg width={17} height={17} viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 3a6 6 0 016 6v4l2 3H4l2-3V9a6 6 0 016-6z" stroke="#fff" strokeWidth={2} strokeLinejoin="round" />
              <path d="M10 19a2 2 0 004 0" stroke="#fff" strokeWidth={2} />
            </svg>
          </div>
        </div>
        {/* A door, not a field: B1's own search bar opens `/search` the same way. */}
        <button
          type="button"
          onClick={onSearch}
          className="w-full bg-white h-[50px] flex items-center px-[13px] py-0 gap-[11px] text-left border-0 rounded-none"
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
            <circle cx="11" cy="11" r="7" stroke="#5B6B69" strokeWidth={2} />
            <path d="M16 16l4 4" stroke="#5B6B69" strokeWidth={2} />
          </svg>
          <span className="flex-1 font-anek font-normal text-[16px] leading-[1.75] text-muted">
            {d.search_placeholder_bn}
          </span>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
            <rect x="9" y="3" width="6" height="11" rx="3" stroke="#0E3A45" strokeWidth={2} />
            <path d="M5 11a7 7 0 0014 0M12 18v3" stroke="#0E3A45" strokeWidth={2} />
          </svg>
        </button>
      </div>

      <div className="p-[16px] flex-1 oprappo-scroll">
        <div className="grid grid-cols-4 gap-x-[8px] gap-y-[12px]">
          {d.quick_actions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={onQuickAction === undefined ? undefined : () => onQuickAction(a.id)}
              className="text-center bg-transparent border-0 p-0 rounded-none"
            >
              <div className="h-[42px] flex items-center justify-center">
                <QuickGlyph id={a.id} />
              </div>
              <div className="font-anek font-medium text-[13px] leading-[1.75] text-ink2">{a.label_bn}</div>
            </button>
          ))}
        </div>

        <div className="bg-white border border-border p-[16px] mt-[22px]">
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{d.card_title_bn}</div>
          <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[7px]">
            {d.card_body_bn}
          </div>
        </div>
      </div>

      <TabBar activeKey="home" onChange={onTab} />
    </StateFrame>
  );
}

/* ─────────────────────────────────────────────────────────────────── § ২ ── */

function NewUserActivity({ onAction, onTab }: Handlers): JSX.Element {
  const d = NEW_USER_ACTIVITY;
  return (
    <StateFrame stateKey="new_user">
      {/* Tabs stay visible so the screen's structure is learned before there is
          content in it (frame note). */}
      <div className="bg-white px-[16px] pt-[16px] border-b border-border">
        <div className="font-anek font-semibold text-[22px] leading-[1.25] text-ink mb-[14px]">{d.title_bn}</div>
        <div role="tablist" className="flex">
          {d.tabs.map((t, i) => (
            <div
              key={t.id}
              role="tab"
              aria-selected={i === 0}
              className={
                'px-[3px] pb-[11px] font-anek text-[15px] leading-[1.75]' +
                (i < d.tabs.length - 1 ? ' mr-[22px]' : '') +
                (i === 0 ? ' font-semibold text-teal border-b-2 border-teal mb-[-1px]' : ' font-medium text-muted')
              }
            >
              {t.label_bn}
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-[24px] text-center">
        {/* "The icon is the tab's own glyph in border grey — no separate illustration
            to commission." `#C4BFB2` is borderWarm, used here as a stroke. */}
        <svg width={52} height={52} viewBox="0 0 24 24" fill="none" aria-hidden className="mb-[20px]">
          <path d="M4 6h16M4 12h16M4 18h10" stroke="#C4BFB2" strokeWidth={2} strokeLinecap="round" />
        </svg>
        <div className="font-anek font-semibold text-[20px] leading-[1.75] text-ink">{d.empty_title_bn}</div>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[9px]">
          {d.empty_body_bn}
        </div>
        <button
          type="button"
          onClick={onAction}
          className="bg-teal px-[22px] py-[14px] font-anek font-semibold text-[15px] leading-[1.75] text-white mt-[24px] border-0 rounded-none"
        >
          {d.empty_action_bn}
        </button>
      </div>

      <TabBar activeKey="activity" onChange={onTab} />
    </StateFrame>
  );
}

/* ─────────────────────────────────────────────────────────────────── § ৪ ── */

/**
 * An AI conversation with nothing in it yet: the header, an empty body and the composer.
 * The drawn exchange between them is gone — see `data.ts § ৪`. The composer is a door to
 * the AI screen, where the person can actually type; this frame never could.
 */
function NewUserAi({ onAction, onBack }: Handlers): JSX.Element {
  const d = NEW_USER_AI;
  return (
    <StateFrame stateKey="new_user">
      <div className="bg-white border-b border-border px-[16px] py-[13px] flex gap-[11px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
        <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
          <path
            d="M12 3l2.2 6.2L20.5 11l-6.3 1.8L12 19l-2.2-6.2L3.5 11l6.3-1.8z"
            stroke="#8A5A0C"
            strokeWidth={2}
            strokeLinejoin="round"
          />
        </svg>
        <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">{d.title_bn}</div>
      </div>

      <div className="flex-1 p-[16px] flex flex-col oprappo-scroll" />

      <div className="bg-white border-t border-border px-[16px] py-[11px] flex gap-[10px] items-center">
        <button
          type="button"
          onClick={onAction}
          className="flex-1 box-content border border-border bg-white h-[46px] flex items-center px-[13px] py-0 text-left font-anek font-normal text-[15px] leading-[1.75] text-muted rounded-none"
        >
          {d.composer_placeholder_bn}
        </button>
        <button
          type="button"
          aria-label={ariaLabel.send}
          onClick={onAction}
          className="w-[42px] h-[42px] bg-teal flex items-center justify-center flex-none border-0 p-0 rounded-none"
        >
          <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M4 12h14M13 7l5 5-5 5" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </StateFrame>
  );
}

export function NewUserState({ variant, ...handlers }: NewUserStateProps): JSX.Element {
  if (variant === 'activity') return <NewUserActivity {...handlers} />;
  if (variant === 'ai') return <NewUserAi {...handlers} />;
  return <NewUserHome {...handlers} />;
}

/** Which drawn variant a surface uses. Account and internal have no drawn empty. */
export function newUserVariantFor(surface: string): NewUserVariant {
  if (surface === 'activity') return 'activity';
  if (surface === 'ai') return 'ai';
  return 'home';
}
